/* eslint-disable react/no-find-dom-node */
import React, {
  FunctionComponent,
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useForm, Controller, FieldValues } from 'react-hook-form'
import { useDispatch, useSelector, RootStateOrAny } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useDebouncedCallback } from 'use-debounce'
import Alert from 'Shared/components/Alert'
import alertGreyIcon from 'Shared/img/svg/alert-grey.svg'
import { reviewMadeEvent, trackAddedToPlaylistEvent } from 'Helpers/tagManager'
import { sendFeedReview, skipFeedReview } from 'Redux/actions/feedActions'
// import { sendReview, skipReview } from 'Redux/actions/reviewsActions'
import { getAwaitingTracksSelector } from 'Redux/selectors/feed'
import { getUserId } from 'Redux/selectors/auth'
import WarningSvg from 'Shared/img/Warning.svg'
import { reviewTrackSchema } from 'Helpers/validation'
import {
  ReviewSelectValue,
  ReviewType,
  CampaignPlatformTypeEnum,
} from 'Types/index'

import { SelectPlaylist } from '../SelectPlaylist'

import { ReviewFormValues, ReviewTrackFormProps } from './props'
import {
  Container,
  SectionTitle,
  TextArea,
  StyledSpace,
  ButtonsContainer,
  Button,
  ButtonSimple,
  Warning,
  WarningIcon,
  WarningText,
  HiddenPlaylistsContainer,
  HiddenPlaylistsIcon,
  HiddenPlaylistsText,
} from './styles'

export const ReviewTrackForm: FunctionComponent<ReviewTrackFormProps> = ({
  playlistFromWarning,
  setModalHeaderGreen,
  setThreeReviewsWarning,
  onSuccess,
  reviewDetails,
  openSkipModal,
  closeReviewModal,
}) => {
  const { t } = useTranslation()
  const userId = useSelector(getUserId)
  const dispatch = useDispatch()
  const textAreaRef = useRef(null)
  const [notAuthorized, setNotAuthorized] = useState(false)

  const blacklistWords = useSelector(
    (state: RootStateOrAny) => state.reviews.wordsBlacklist
  )
  const feedTracks = useSelector(getAwaitingTracksSelector)

  const [warningVisible, setWarningVisible] = useState(false)
  const [warningText, setWarningText] = useState('')
  const [isDisabledSubmit, setIsDisabledSubmit] = useState(false)

  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault()
      setWarningText(t('reviewsPage.uniqueReviewRequired'))
      setWarningVisible(true)
    }
    const field = ReactDOM.findDOMNode(textAreaRef.current)

    field?.addEventListener('paste', handler)
    field?.addEventListener('copy', handler)
    field?.addEventListener('cut', handler)

    return () => {
      field?.removeEventListener('paste', handler)
      field?.removeEventListener('copy', handler)
      field?.removeEventListener('cut', handler)
    }
  }, [])

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FieldValues>({
    mode: 'onChange',
    resolver: yupResolver<yup.AnyObjectSchema>(
      reviewTrackSchema(blacklistWords)
    ),
  })

  const values = getValues()
  const addAction = values?.reviewPlaylistSelect?.type === 'add'
  const rejectionAction = values?.reviewPlaylistSelect?.type === 'reject'
  const actionId = values?.reviewPlaylistSelect?.id

  const reason = useMemo(() => {
    if (rejectionAction && actionId === 1)
      return 'I do not like the track enough'

    if (rejectionAction && actionId === 2)
      return 'The track does not fit my playlist genre'
    return ''
  }, [rejectionAction, actionId, addAction])

  const watchComment = watch('reviewText')
  const watchSelect: ReviewSelectValue = watch('reviewPlaylistSelect')

  useEffect(() => {
    if (
      watchSelect?.type === ReviewType.reject &&
      reviewDetails.isRejectLastThree
    ) {
      setModalHeaderGreen(false)
      setThreeReviewsWarning(true)
    }
  }, [watchSelect])

  useEffect(() => {
    if (playlistFromWarning) {
      setValue('reviewPlaylistSelect', playlistFromWarning, {
        shouldValidate: true,
      })
    }
  }, [playlistFromWarning])

  const onSubmit = (values: ReviewFormValues) => {
    setIsDisabledSubmit(true)
    dispatch(
      sendFeedReview({
        campaign_id: reviewDetails.campaign_id,
        comment: values.reviewText,
        value: values.reviewPlaylistSelect,
        callback: (msg, error) => {
          reviewMadeEvent(
            reviewDetails.campaign_id,
            CampaignPlatformTypeEnum.SPOTIFY,
            addAction,
            userId,
            new Date().getTime(),
            Number(reviewDetails.potential_reward),
            !addAction && actionId,
            values.reviewText,
            true
          )

          addAction &&
            trackAddedToPlaylistEvent(
              reviewDetails.campaign_id,
              CampaignPlatformTypeEnum.SPOTIFY,
              userId,
              new Date().getTime(),
              Number(reviewDetails.potential_reward),
              Number(reviewDetails.track.id),
              true
            )

          // @ts-ignore
          if (error?.response?.status === 403) {
            setIsDisabledSubmit(false)
            setNotAuthorized(true)
          }
          if (!msg) {
            setNotAuthorized(false)
            setIsDisabledSubmit(false)
            onSuccess()
          }
          setNotAuthorized(false)
          setIsDisabledSubmit(false)
          onSuccess()
        },
      })
    )
  }

  const [debouncedOnSubmit] = useDebouncedCallback(
    (values: ReviewFormValues) => onSubmit(values),
    1000
  )

  const isHiddenPlaylists = useMemo(() => {
    const campaignFromList = feedTracks.find(el => el.campaign.matchedPlaylists)
    const playlistsFromList = campaignFromList?.campaign.matchedPlaylists
      .sort()
      .join('')
    const playlistsFromDetails = reviewDetails.matched_playlists
      .map(el => el.playlist_name)
      .sort()
      .join('')
    return playlistsFromList !== playlistsFromDetails
  }, [feedTracks, reviewDetails])

  return (
    <Container onSubmit={handleSubmit(debouncedOnSubmit)}>
      <SectionTitle>{t('reviewsPage.addToPlaylist')}</SectionTitle>
      <Controller
        control={control}
        name="reviewPlaylistSelect"
        render={({ field: { onChange, value, ref } }) => (
          <SelectPlaylist ref={ref} value={value} onChange={onChange} />
        )}
      />
      {isHiddenPlaylists && (
        <HiddenPlaylistsContainer>
          <HiddenPlaylistsIcon src={alertGreyIcon} alt="" />
          <HiddenPlaylistsText>
            We hid some playlists as it don`t fit this track
          </HiddenPlaylistsText>
        </HiddenPlaylistsContainer>
      )}
      <SectionTitle>{t('reviewsPage.writeReview')}</SectionTitle>
      <Controller
        control={control}
        name="reviewText"
        render={({ field }) => (
          <TextArea
            {...field}
            placeholder={t('reviewsPage.commentPlaceholder')}
            ref={textAreaRef}
          />
        )}
      />
      {(warningVisible || errors.reviewText) && (
        <Warning>
          <WarningIcon src={WarningSvg} />
          <WarningText>{errors.reviewText?.message || warningText}</WarningText>
        </Warning>
      )}
      <ButtonsContainer>
        {/* {reviewDetails.daysLeft > 5 && ( */}
        <ButtonSimple
          onClick={() =>
            openSkipModal({
              campaign_id: reviewDetails.campaign_id,
            })
          }
        >
          {t('reviewsPage.skipReview')}
        </ButtonSimple>
        {/* )} */}
        <Button
          disabled={!watchSelect || !watchComment || isDisabledSubmit}
          type="submit"
        >
          {t('reviewsPage.sendReview')}
        </Button>
      </ButtonsContainer>
      {notAuthorized && (
        <Alert color="danger" icon>
          <div className="alert__text">
            <div className="alert__title">
              {t('reviewsPage.unathorizedMessage')}
            </div>
          </div>
        </Alert>
      )}
    </Container>
  )
}
